import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Eye, Calendar, Clock, Users, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Modal para visualizar o relatório de acessos a um documento
 * Similar ao HorarioRelatorioAcessosModal
 */
const DocumentosRelatorioAcessosModal = ({ 
  show, 
  relatorioAcessos, 
  onClose 
}) => {
  const [historicoColabExpandido, setHistoricoColabExpandido] = useState(null);

  if (!show || !relatorioAcessos) return null;

  const formatarData = (dataStr) => {
    if (!dataStr) return '-';
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString('pt-PT', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dataStr;
    }
  };

  const formatarHora = (dataStr) => {
    if (!dataStr) return '-';
    try {
      const data = new Date(dataStr);
      return data.toLocaleTimeString('pt-PT', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-800">
            <Eye className="w-5 h-5 text-blue-600" />
            Relatório de Acessos ao Documento
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Resumo */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-blue-600">{relatorioAcessos.total_colaboradores}</div>
              <div className="text-sm text-gray-600">Colaboradores viram</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Eye className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-green-600">{relatorioAcessos.total_acessos}</div>
              <div className="text-sm text-gray-600">Total de acessos</div>
            </div>
          </div>
          
          {/* Lista por colaborador */}
          <div className="space-y-2">
            <h3 className="font-medium text-sm text-gray-700 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Detalhes por Colaborador
            </h3>
            
            {relatorioAcessos.por_colaborador?.length > 0 ? (
              relatorioAcessos.por_colaborador.map((colab, idx) => (
                <div key={idx} className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setHistoricoColabExpandido(historicoColabExpandido === colab.colaborador_id ? null : colab.colaborador_id)}
                  >
                    <div>
                      <div className="font-semibold text-gray-800">{colab.colaborador_nome}</div>
                      <div className="text-sm text-gray-600">
                        Último acesso: {colab.ultimo_acesso ? new Date(colab.ultimo_acesso).toLocaleString('pt-PT') : '-'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-blue-600">{colab.total_acessos} acesso(s)</div>
                      {colab.tempo_total_formatado && colab.tempo_total_formatado !== '-' && (
                        <div className="text-xs text-green-600 flex items-center gap-1 justify-end">
                          <Clock className="w-3 h-3" />
                          {colab.tempo_total_formatado}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 flex items-center gap-1 justify-end mt-1">
                        {historicoColabExpandido === colab.colaborador_id ? (
                          <>
                            <ChevronUp className="w-3 h-3" />
                            Fechar
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3 h-3" />
                            Ver datas
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Histórico expandido */}
                  {historicoColabExpandido === colab.colaborador_id && (
                    <div className="bg-white p-3 border-t max-h-48 overflow-y-auto">
                      <div className="text-sm font-medium mb-2 flex items-center gap-2 text-gray-700">
                        <Calendar className="w-4 h-4" />
                        Histórico de Acessos:
                      </div>
                      <div className="space-y-1">
                        {colab.acessos?.map((acesso, aIdx) => (
                          <div key={aIdx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <span>📆</span>
                              <span>
                                {formatarData(acesso.data_hora)}
                              </span>
                              <span className="text-gray-400">às</span>
                              <span className="font-medium text-blue-600">
                                {formatarHora(acesso.data_hora)}
                              </span>
                            </div>
                            {acesso.tempo_formatado && (
                              <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded">
                                <Clock className="w-3 h-3" />
                                <span className="font-medium">{acesso.tempo_formatado}</span>
                              </div>
                            )}
                          </div>
                        ))}
                        {(!colab.acessos || colab.acessos.length === 0) && (
                          <div className="text-center py-2 text-gray-500 text-sm">
                            Sem detalhes de acesso
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Nenhum acesso registado ainda</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <Button onClick={onClose} className="w-full bg-cyan-500 hover:bg-cyan-600">
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentosRelatorioAcessosModal;
